import { useQueries } from '@tanstack/react-query';

import { pageListQueryOptions } from '@/features/pages';
import { session } from '@/shared/session/session';

export interface SizeGuideOption {
  handle: string;
  /** Page titles, with the store when several stores have a page under the handle. */
  label: string;
}

/**
 * Size sets are organization-wide but pages belong to stores, so the handle a set points at is
 * looked up in every store; each store resolves it against its own `size_guide` page.
 */
export function useSizeGuidePages(): SizeGuideOption[] {
  const { me } = session.useSession();
  const stores = me?.stores ?? [];
  const results = useQueries({
    queries: stores.map((store) => ({
      ...pageListQueryOptions(store.id, { template: 'size_guide', limit: 100 }),
      throwOnError: false,
      meta: { silentError: true },
    })),
  });
  const byHandle = new Map<string, string[]>();
  results.forEach((result, index) => {
    const store = stores[index];
    for (const page of result.data?.items ?? []) {
      const titles = byHandle.get(page.handle) ?? [];
      titles.push(stores.length > 1 && store ? `${page.title} · ${store.name}` : page.title);
      byHandle.set(page.handle, titles);
    }
  });
  return [...byHandle.entries()]
    .map(([handle, titles]) => ({ handle, label: titles.join(', ') }))
    .sort((a, b) => a.handle.localeCompare(b.handle));
}
